import React from 'react';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicy = () => {
  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px'
  };

  const pageWrapperStyle = {
    paddingTop: '80px',
    paddingBottom: '80px'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '60px',
    paddingBottom: '40px',
    borderBottom: '3px solid #52A7B0',
    animation: 'fadeIn 0.8s ease-in'
  };

  const headerTitleStyle = {
    fontSize: '2.8rem',
    color: '#1a4a5a',
    marginBottom: '10px',
    fontWeight: '700',
    margin: '0 0 15px 0',
    letterSpacing: '-0.5px'
  };

  const headerSubtitleStyle = {
    fontSize: '1rem',
    color: '#666',
    margin: '0',
    fontWeight: '400'
  };

  const contentStyle = {
    backgroundColor: '#fafafa',
    padding: '50px 50px',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e8f0f2'
  };

  const sectionStyle = {
    marginBottom: '45px',
    paddingBottom: '35px',
    borderBottom: '1px solid #e5e5e5'
  };

  const lastSectionStyle = {
    ...sectionStyle,
    borderBottom: 'none',
    marginBottom: '0',
    paddingBottom: '0'
  };

  const h2Style = {
    fontSize: '1.6rem',
    color: '#1a4a5a',
    marginBottom: '20px',
    marginTop: '0',
    fontWeight: '700',
    lineHeight: '1.4',
    paddingBottom: '10px',
    borderBottom: '2px solid #52A7B0'
  };

  const h3Style = {
    fontSize: '1.25rem',
    color: '#306873',
    marginTop: '28px',
    marginBottom: '18px',
    fontWeight: '700',
    letterSpacing: '0.3px'
  };

  const pStyle = {
    fontSize: '0.95rem',
    color: '#444',
    marginBottom: '18px',
    lineHeight: '1.9'
  };

  const ulStyle = {
    margin: '20px 0',
    paddingLeft: '28px',
    listStyle: 'none'
  };

  const liStyle = {
    marginBottom: '16px',
    paddingLeft: '24px',
    position: 'relative',
    color: '#444',
    fontSize: '0.95rem',
    lineHeight: '1.8',
    transition: 'transform 0.2s ease'
  };

  const strongStyle = {
    color: '#1a4a5a',
    fontWeight: '600'
  };

  const contactInfoStyle = {
    backgroundColor: 'linear-gradient(135deg, #e8f4f6 0%, #f0f9fb 100%)',
    backgroundImage: 'linear-gradient(135deg, #e8f4f6 0%, #f0f9fb 100%)',
    padding: '30px',
    borderLeft: '6px solid #52A7B0',
    borderRadius: '8px',
    margin: '30px 0',
    boxShadow: '0 2px 8px rgba(82, 167, 176, 0.1)'
  };

  const linkStyle = {
    color: '#52A7B0',
    textDecoration: 'none',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    borderBottom: '2px solid transparent'
  };

  const PolicySection = ({ title, children }) => (
    <section style={sectionStyle}>
      <h2 style={h2Style}>{title}</h2>
      {children}
    </section>
  );

  const LastPolicySection = ({ title, children }) => (
    <section style={lastSectionStyle}>
      <h2 style={h2Style}>{title}</h2>
      {children}
    </section>
  );

  const ListItem = ({ children }) => (
    <li style={liStyle}>
      <span style={{position: 'absolute', left: '0', color: '#52A7B0', fontWeight: 'bold'}}>→</span>
      {children}
    </li>
  );

  return (
    <>
      <Helmet>
        <title>Privacy Policy - WorkdeskVA Remote Staffing Solutions</title>
        <meta name="description" content="Privacy policy for WorkdeskVA - protecting your data and privacy in remote staffing services." />
      </Helmet>
      <div style={pageWrapperStyle}>
        <div style={containerStyle}>
          <div style={headerStyle}>
            <h1 style={headerTitleStyle}>Privacy Policy</h1>
            <p style={{...headerSubtitleStyle, fontSize: '1.05rem', color: '#306873', marginTop: '15px'}}>
              Your data protection is our priority. Learn how we collect, use, and protect your information.
            </p>
          </div>

          <div style={contentStyle}>
            <PolicySection title="1. Introduction">
              <p style={{...pStyle, fontSize: '1.05rem', color: '#333', fontWeight: '500'}}>
                <span style={strongStyle}>WorkdeskVA</span> is dedicated to safeguarding your privacy and ensuring transparency in how we handle your data. This comprehensive Privacy Policy outlines our data collection, usage, and protection practices.
              </p>
              <p style={pStyle}>
                We deliver professional remote staffing solutions—including virtual assistants, dedicated teams, and specialized remote talent—across diverse industries. Your trust is paramount, and we're committed to maintaining the highest standards of data privacy and transparent practices.
              </p>
            </PolicySection>

            <PolicySection title="2. Information We Collect">
              <p style={pStyle}>We may collect information about you in a variety of ways. The information we may collect on the site includes:</p>

              <h3 style={h3Style}>2.1 Personal Data You Provide</h3>
              <ul style={ulStyle}>
                <ListItem><span style={strongStyle}>Registration Information:</span> Name, email address, phone number, company name, job title, and location</ListItem>
                <ListItem><span style={strongStyle}>Communication Data:</span> Messages, inquiries, feedback, and support requests you send to us</ListItem>
                <ListItem><span style={strongStyle}>Payment Information:</span> Billing address, payment method details (processed securely through third-party payment processors)</ListItem>
                <ListItem><span style={strongStyle}>Professional Information:</span> Resume, qualifications, work experience (for staff placement)</ListItem>
                <ListItem><span style={strongStyle}>Preference Data:</span> Your staffing needs, industry focus, service preferences</ListItem>
              </ul>

              <h3 style={h3Style}>2.2 Automatically Collected Information</h3>
              <ul style={ulStyle}>
                <ListItem><span style={strongStyle}>Log Data:</span> IP address, browser type, pages visited, time and date of visits</ListItem>
                <ListItem><span style={strongStyle}>Device Information:</span> Device type, operating system, unique device identifiers</ListItem>
                <ListItem><span style={strongStyle}>Cookies & Tracking:</span> Usage patterns through cookies and similar technologies</ListItem>
                <ListItem><span style={strongStyle}>Analytics Data:</span> Interaction with our website and services through analytics tools</ListItem>
              </ul>

              <h3 style={h3Style}>2.3 Information from Third Parties</h3>
              <ul style={ulStyle}>
                <ListItem>Referral information if you're referred by a partner or client</ListItem>
                <ListItem>Background check and verification data (with consent, for staffing purposes)</ListItem>
                <ListItem>Information from industry databases for talent vetting</ListItem>
              </ul>
            </PolicySection>

            <PolicySection title="3. How We Use Your Information">
              <p style={pStyle}>WorkdeskVA uses the collected information for various purposes, including:</p>
              <ul style={ulStyle}>
                <ListItem>Providing and improving our remote staffing services</ListItem>
                <ListItem>Matching qualified remote talent with client requirements</ListItem>
                <ListItem>Processing orders and sending related information</ListItem>
                <ListItem>Sending promotional emails, newsletters, and service updates (with consent)</ListItem>
                <ListItem>Responding to your inquiries and customer support requests</ListItem>
                <ListItem>Conducting background checks and vetting processes for staff placements</ListItem>
                <ListItem>Analyzing website usage to enhance user experience</ListItem>
                <ListItem>Detecting and preventing fraud and security incidents</ListItem>
                <ListItem>Complying with legal obligations and regulatory requirements</ListItem>
                <ListItem>Personalizing content and recommendations based on your preferences</ListItem>
              </ul>
            </PolicySection>

            <PolicySection title="4. How We Protect Your Information">
              <p style={pStyle}>
                WorkdeskVA implements comprehensive security measures to protect your personal information:
              </p>
              <ul style={ulStyle}>
                <ListItem><span style={strongStyle}>Encryption:</span> SSL/TLS encryption for all data in transit</ListItem>
                <ListItem><span style={strongStyle}>Access Controls:</span> Limited access to personal data, restricted to authorized personnel only</ListItem>
                <ListItem><span style={strongStyle}>Data Minimization:</span> We collect only the information necessary for our services</ListItem>
                <ListItem><span style={strongStyle}>Regular Audits:</span> Periodic security assessments and vulnerability testing</ListItem>
                <ListItem><span style={strongStyle}>Secure Servers:</span> Industry-standard firewalls and secure data storage</ListItem>
                <ListItem><span style={strongStyle}>Employee Training:</span> All staff trained on data privacy and security best practices</ListItem>
                <ListItem><span style={strongStyle}>Incident Response:</span> Procedures in place to respond to any data breaches</ListItem>
              </ul>
              <p style={pStyle}>
                While we strive to use commercially acceptable means to protect your personal information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
              </p>
            </PolicySection>

            <PolicySection title="5. Disclosure of Your Information">
              <p style={pStyle}>We may share your information in the following circumstances:</p>
              <ul style={ulStyle}>
                <ListItem><span style={strongStyle}>Service Providers:</span> Third-party vendors who assist in our operations (payment processors, hosting providers, analytics services)</ListItem>
                <ListItem><span style={strongStyle}>Business Partners:</span> With your consent, to relevant parties for staffing placements and service delivery</ListItem>
                <ListItem><span style={strongStyle}>Legal Requirements:</span> When required by law or to protect our legal rights</ListItem>
                <ListItem><span style={strongStyle}>Business Transfers:</span> If WorkdeskVA is involved in a merger, acquisition, or asset sale</ListItem>
                <ListItem><span style={strongStyle}>With Your Consent:</span> Any other disclosure with your explicit permission</ListItem>
              </ul>
              <p style={pStyle}>
                We do not sell your personal information to third parties for their independent marketing purposes without your explicit consent.
              </p>
            </PolicySection>

            <PolicySection title="6. Cookies and Tracking Technologies">
              <p style={pStyle}>
                Our website uses cookies and similar tracking technologies to enhance your browsing experience and analyze site usage:
              </p>
              <ul style={ulStyle}>
                <ListItem><span style={strongStyle}>Essential Cookies:</span> Necessary for website functionality and security</ListItem>
                <ListItem><span style={strongStyle}>Performance Cookies:</span> Help us understand how visitors use our site</ListItem>
                <ListItem><span style={strongStyle}>Functional Cookies:</span> Remember your preferences and settings</ListItem>
                <ListItem><span style={strongStyle}>Marketing Cookies:</span> Track your interests for targeted advertising</ListItem>
              </ul>
              <p style={pStyle}>
                You can control cookie preferences through your browser settings. Note that disabling certain cookies may affect website functionality.
              </p>
            </PolicySection>

            <PolicySection title="7. Your Privacy Rights">
              <p style={pStyle}>Depending on your location, you may have the following rights regarding your personal data:</p>
              <ul style={ulStyle}>
                <ListItem><span style={strongStyle}>Right to Access:</span> Request what personal data we hold about you</ListItem>
                <ListItem><span style={strongStyle}>Right to Rectification:</span> Correct inaccurate or incomplete information</ListItem>
                <ListItem><span style={strongStyle}>Right to Erasure:</span> Request deletion of your data (subject to legal obligations)</ListItem>
                <ListItem><span style={strongStyle}>Right to Restrict:</span> Limit how we process your information</ListItem>
                <ListItem><span style={strongStyle}>Right to Portability:</span> Receive your data in a portable format</ListItem>
                <ListItem><span style={strongStyle}>Right to Object:</span> Opt-out of certain processing activities</ListItem>
                <ListItem><span style={strongStyle}>Right to Withdraw Consent:</span> Withdraw previously given consent at any time</ListItem>
              </ul>
              <p style={pStyle}>
                To exercise any of these rights, please contact us using the information provided in the "Contact Us" section below.
              </p>
            </PolicySection>

            <PolicySection title="8. Data Retention">
              <p style={pStyle}>
                We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this policy. Retention periods vary based on the type of information and its use:
              </p>
              <ul style={ulStyle}>
                <ListItem><span style={strongStyle}>Account Information:</span> Retained for the duration of your engagement plus 5 years for legal compliance</ListItem>
                <ListItem><span style={strongStyle}>Payment Records:</span> Maintained for 7 years for tax and accounting purposes</ListItem>
                <ListItem><span style={strongStyle}>Communications:</span> Retained for 2 years for customer service and dispute resolution</ListItem>
                <ListItem><span style={strongStyle}>Analytics Data:</span> Aggregated and anonymized after 12 months</ListItem>
              </ul>
              <p style={pStyle}>
                When information is no longer needed, we securely delete or anonymize it. You can request data deletion at any time, subject to legal requirements.
              </p>
            </PolicySection>

            <PolicySection title="9. International Data Transfers">
              <p style={pStyle}>
                WorkdeskVA provides remote staffing services internationally. Your personal information may be transferred to, stored in, and processed in countries other than your country of residence. These countries may have data protection laws different from your home country.
              </p>
              <p style={pStyle}>
                When we transfer data internationally, we implement appropriate safeguards such as:
              </p>
              <ul style={ulStyle}>
                <ListItem>Standard contractual clauses approved by relevant authorities</ListItem>
                <ListItem>Binding corporate rules</ListItem>
                <ListItem>Your explicit consent where required</ListItem>
              </ul>
              <p style={pStyle}>
                By using our services, you consent to the transfer of your information to countries outside your country of residence.
              </p>
            </PolicySection>

            <PolicySection title="10. Third-Party Links">
              <p style={pStyle}>
                Our website may contain links to third-party websites, including social media platforms and service providers. We are not responsible for the privacy practices of external sites. We encourage you to review their privacy policies before providing personal information.
              </p>
            </PolicySection>

            <PolicySection title="11. Children's Privacy">
              <p style={pStyle}>
                WorkdeskVA does not knowingly collect information from children under 18 years of age. Our services are intended for adults and businesses. If we become aware that a child has provided us with personal information, we will promptly delete such information and terminate the child's account if applicable.
              </p>
              <p style={pStyle}>
                If you believe we have collected information from a child, please contact us immediately using the details provided below.
              </p>
            </PolicySection>

            <PolicySection title="12. GDPR Compliance">
              <p style={pStyle}>
                For users in the European Union, we comply with the General Data Protection Regulation (GDPR). We:
              </p>
              <ul style={ulStyle}>
                <ListItem>Obtain explicit consent for data processing</ListItem>
                <ListItem>Provide clear information about our data practices</ListItem>
                <ListItem>Ensure data minimization and purpose limitation</ListItem>
                <ListItem>Maintain records of processing activities</ListItem>
                <ListItem>Respond to data subject requests within 30 days</ListItem>
                <ListItem>Report data breaches to authorities within 72 hours when required</ListItem>
              </ul>
            </PolicySection>

            <PolicySection title="13. CCPA Compliance (California Residents)">
              <p style={pStyle}>
                If you are a California resident, the California Consumer Privacy Act (CCPA) provides you with additional rights:
              </p>
              <ul style={ulStyle}>
                <ListItem>Right to know what personal information is collected and how it's used</ListItem>
                <ListItem>Right to delete personal information collected from you</ListItem>
                <ListItem>Right to opt-out of the sale or sharing of your personal information</ListItem>
                <ListItem>Right to non-discrimination for exercising your CCPA rights</ListItem>
              </ul>
              <p style={pStyle}>
                To exercise these rights, please submit a verifiable consumer request to our contact information below.
              </p>
            </PolicySection>

            <PolicySection title="14. Marketing Communications">
              <p style={pStyle}>
                We may send you marketing communications about our services, special offers, and industry updates. You can manage your communication preferences by:
              </p>
              <ul style={ulStyle}>
                <ListItem>Clicking "unsubscribe" links in our emails</ListItem>
                <ListItem>Updating your notification settings in your account</ListItem>
                <ListItem>Contacting us directly to opt-out</ListItem>
              </ul>
              <p style={pStyle}>
                We will honor your preferences, though we may still send service-related announcements and administrative messages.
              </p>
            </PolicySection>

            <PolicySection title="15. Policy Updates">
              <p style={pStyle}>
                WorkdeskVA may update this Privacy Policy periodically to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of material changes by:
              </p>
              <ul style={ulStyle}>
                <ListItem>Posting the revised policy on our website with an updated "Last Updated" date</ListItem>
                <ListItem>Sending an email notification for significant changes</ListItem>
                <ListItem>Requesting your consent if required by applicable law</ListItem>
              </ul>
              <p style={pStyle}>
                Your continued use of our services following the posting of revised Privacy Policy means you accept and agree to the changes.
              </p>
            </PolicySection>

            <PolicySection title="16. Contact Us">
              <p style={pStyle}>
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div style={contactInfoStyle}>
                <p style={{...pStyle, margin: '0'}}>
                  <span style={strongStyle}>WorkdeskVA</span><br />
                  Email: <a href="mailto:privacy@workdeskva.com" style={linkStyle}>privacy@workdeskva.com</a><br />
                  Website: <a href="https://www.workdeskva.com" style={linkStyle}>www.workdeskva.com</a>
                </p>
              </div>
              <p style={pStyle}>
                We will respond to your inquiry within 30 days. If you are not satisfied with our response, you may lodge a complaint with the relevant data protection authority in your jurisdiction.
              </p>
            </PolicySection>

            <PolicySection title="17. Data Protection Officer">
              <p style={pStyle}>
                For organizations subject to GDPR and similar regulations, we designate a Data Protection Officer (DPO) responsible for overseeing our data protection practices. For DPO inquiries, contact us at the email address provided above.
              </p>
            </PolicySection>

            <LastPolicySection title="18. Additional Information">
              <p style={pStyle}>
                <span style={strongStyle}>Supervisory Authority:</span> If you are an EU resident and believe we have violated your rights under GDPR, you have the right to lodge a complaint with your local supervisory authority.
              </p>
              <p style={pStyle}>
                <span style={strongStyle}>Data Processing Agreement:</span> Organizations using our B2B services can request a Data Processing Agreement (DPA) that outlines specific data handling terms.
              </p>
              <p style={{...pStyle, marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #ddd'}}>
                <span style={strongStyle}>Acknowledgment:</span> By using WorkdeskVA's services, you acknowledge that you have read and understood this Privacy Policy and agree to our data practices.
              </p>
            </LastPolicySection>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
